import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { enrollmentService } from '../services';
import {
  BookOpen, MapPin, Calendar, User, AlertCircle, Loader2,
  ChevronRight, CheckCircle, Clock, PlayCircle, FileText,
} from 'lucide-react';

// ─── Status config ─────────────────────────────────────────────────────────
const STATUS_STYLES = {
  active:          'bg-green-100 text-green-700',
  pending_payment: 'bg-amber-100 text-amber-700',
  completed:       'bg-blue-100 text-blue-700',
  cancelled:       'bg-red-100 text-red-600',
};

// ─── Progress ring (SVG) ───────────────────────────────────────────────────
function ProgressRing({ value, max, size = 52, stroke = 5 }) {
  const pct    = max > 0 ? Math.min(value / max, 1) : 0;
  const r      = (size - stroke) / 2;
  const circ   = 2 * Math.PI * r;
  const offset = circ * (1 - pct);
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E0E7FF" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#3B82F6"
        strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
        fill="#1e40af" fontSize="11" fontWeight="700"
        style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
        {Math.round(pct * 100)}%
      </text>
    </svg>
  );
}

// ─── Coaching course card ─────────────────────────────────────────────────
function CourseCard({ course, isHighlighted, onClick }) {
  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const startDate = fmt(course.start_date);
  const endDate   = fmt(course.end_date);
  const sessions  = course.total_sessions    ?? null;
  const doneSess  = course.completed_sessions ?? null;
  const homework  = course.total_homework     ?? null;
  const doneHW    = course.homework_submitted  ?? null;
  const isCompleted = course.status === 'completed';

  return (
    <div
      data-testid="coaching-course-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick()}
      className={`group bg-white rounded-3xl border shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-400
        ${isHighlighted ? 'border-blue-400 ring-2 ring-blue-200' : 'border-slate-200/60 hover:border-blue-200'}`}
    >
      {/* colour stripe */}
      <div className={`h-1.5 bg-gradient-to-r ${isCompleted ? 'from-blue-300 to-indigo-400' : 'from-blue-500 to-indigo-600'}`} />

      <div className="p-7">
        {/* Icon + status */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform ${isCompleted ? 'bg-blue-50' : 'bg-blue-50'}`}>
            <span className="text-2xl">📘</span>
          </div>
          {course.status && (
            <span className={`text-xs font-bold px-3 py-1 rounded-full capitalize flex-shrink-0 ${STATUS_STYLES[course.status] || 'bg-slate-100 text-slate-500'}`}>
              {course.status.replace('_', ' ')}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="font-bold text-slate-900 text-lg font-outfit mb-0.5 leading-snug">{course.name}</h3>
        {course.program_name && (
          <p className="text-blue-500 text-xs font-semibold mb-3">{course.program_name}</p>
        )}
        {course.description && (
          <p className="text-slate-500 text-sm mb-4 line-clamp-2">{course.description}</p>
        )}

        {/* Meta */}
        <div className="space-y-2 text-sm text-slate-500 mb-5">
          {course.participant_name && (
            <div className="flex items-center gap-2">
              <User size={14} className="text-slate-400 flex-shrink-0" />
              <span className="truncate">{course.participant_name}</span>
            </div>
          )}
          {course.location && (
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-slate-400 flex-shrink-0" />
              <span className="truncate">{course.location}</span>
            </div>
          )}
          {(startDate || endDate) && (
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-slate-400 flex-shrink-0" />
              <span>{startDate}{endDate && startDate !== endDate ? ` — ${endDate}` : ''}</span>
            </div>
          )}
        </div>

        {/* Progress stats */}
        {(sessions !== null || homework !== null) && (
          <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-2xl mb-5">
            {sessions !== null && (
              <div className="flex items-center gap-2 flex-1">
                <ProgressRing value={doneSess ?? 0} max={sessions} />
                <div>
                  <p className="text-xs font-bold text-slate-700">{doneSess}/{sessions}</p>
                  <p className="text-xs text-slate-400">Lectures</p>
                </div>
              </div>
            )}
            {homework !== null && (
              <div className="flex items-center gap-2 flex-1">
                <div className="w-13 h-13 flex flex-col items-center justify-center bg-indigo-50 rounded-xl px-3 py-1.5">
                  <span className="text-indigo-700 font-bold text-sm">{doneHW}/{homework}</span>
                  <span className="text-indigo-400 text-xs">HW</span>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-700">{doneHW} submitted</p>
                  <p className="text-xs text-slate-400">Assignments</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-blue-500 text-sm font-semibold group-hover:gap-2.5 transition-all">
            {isCompleted
              ? <><CheckCircle size={15} /> View Results</>
              : <><PlayCircle size={15} /> View Lectures</>
            }
            <ChevronRight size={14} />
          </div>
          {isHighlighted && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-semibold">
              Selected
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────
export default function MyCoaching() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const [courses, setCourses]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const courseFilter = new URLSearchParams(location.search).get('course');

  useEffect(() => {
    enrollmentService.getCoachingCourses()
      .then(setCourses)
      .catch(() => setError('Failed to load coaching courses. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleCourseClick = (course) => {
    navigate(`/my-coaching/${course.course_id}`);
  };

  const activeCourses    = courses.filter(c => c.status === 'active' || c.status === 'pending_payment');
  const completedCourses = courses.filter(c => c.status === 'completed' || c.status === 'cancelled');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* ── Page header ─────────────────────────────────────────── */}
        <div className="mb-8">
          <p className="text-blue-500 text-xs font-bold uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
            <span className="w-6 h-px bg-blue-400 inline-block" />
            My Coaching
          </p>
          <h1 className="text-4xl font-bold text-slate-900 font-outfit">📘 My Coaching</h1>
          <p className="text-slate-500 mt-1">SuperTeens Coaching — AI Programs</p>
        </div>

        {/* ── Program banner ──────────────────────────────────────── */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 mb-10 text-white shadow-lg">
          {/* decorative circles */}
          <div className="absolute -right-10 -top-10 w-48 h-48 bg-white/5 rounded-full" />
          <div className="absolute -right-4 -bottom-8 w-32 h-32 bg-white/5 rounded-full" />

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold mb-3">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                SuperTeens Coaching — AI
              </div>
              <h2 className="text-2xl font-bold font-outfit mb-2">Learn from Dr Ernest Wong</h2>
              <p className="text-blue-100 text-sm leading-relaxed">
                Watch all recorded lectures, complete AI-powered assignments, submit projects,
                and get mentored through every step of your learning journey.
              </p>
            </div>
            <div className="flex gap-4 flex-shrink-0">
              {[
                { icon: PlayCircle, label: 'Lectures', sub: 'Recorded' },
                { icon: FileText,   label: 'Homework', sub: '5 Types' },
                { icon: CheckCircle, label: 'Certificate', sub: 'On Completion' },
              ].map(({ icon: Icon, label, sub }) => (
                <div key={label} className="text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-1.5">
                    <Icon size={20} />
                  </div>
                  <p className="text-xs font-bold">{label}</p>
                  <p className="text-blue-200 text-xs">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Error ───────────────────────────────────────────────── */}
        {error && (
          <div data-testid="coaching-error" className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-center gap-3 text-red-700 text-sm">
            <AlertCircle size={16} /> {error}
            <button
              onClick={() => { setError(''); setLoading(true); enrollmentService.getCoachingCourses().then(setCourses).catch(() => setError('Failed')).finally(() => setLoading(false)); }}
              className="ml-auto text-xs font-semibold underline"
            >Retry</button>
          </div>
        )}

        {courses.length === 0 && !loading ? (
          /* ── Empty state ─────────────────────────────────────── */
          <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-14 text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-3xl flex items-center justify-center mx-auto mb-4">
              <BookOpen size={28} className="text-blue-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">No coaching enrollments yet</h3>
            <p className="text-slate-500 text-sm max-w-sm mx-auto">
              Contact your administrator to get enrolled in a SuperTeens Coaching — AI program.
            </p>
          </div>
        ) : (
          <>
            {/* ── Active courses ──────────────────────────────────── */}
            {activeCourses.length > 0 && (
              <section className="mb-10">
                <div className="flex items-center gap-2 mb-5">
                  <Clock size={16} className="text-blue-500" />
                  <h2 className="text-lg font-bold text-slate-900">Active Programs</h2>
                  <span className="text-xs bg-blue-100 text-blue-700 font-bold px-2.5 py-0.5 rounded-full">{activeCourses.length}</span>
                </div>
                <div data-testid="coaching-courses-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {activeCourses.map(course => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      isHighlighted={courseFilter === course.course_id}
                      onClick={() => handleCourseClick(course)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* ── Completed courses ───────────────────────────────── */}
            {completedCourses.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-5">
                  <CheckCircle size={16} className="text-slate-400" />
                  <h2 className="text-lg font-bold text-slate-900">Completed Programs</h2>
                  <span className="text-xs bg-slate-100 text-slate-500 font-bold px-2.5 py-0.5 rounded-full">{completedCourses.length}</span>
                </div>
                <div data-testid="coaching-completed-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-80">
                  {completedCourses.map(course => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      isHighlighted={courseFilter === course.course_id}
                      onClick={() => handleCourseClick(course)}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
